(() => {
  const hceI18n = window.hceI18n || {};
  const DESKTOP_BREAKPOINT = 950;
  const MIN_SCALE = 1;
  const MAX_SCALE = 4;
  const DESKTOP_BUTTON_STEP = 1;
  const MOBILE_BUTTON_STEP = 0.5;
  const WHEEL_STEP = 0.25;

  function initialiseInspectionZoom() {
    const modal = document.getElementById('galModal');
    if (!modal || modal.dataset.hceInspectionZoomReady === 'true') return;

    modal.dataset.hceInspectionZoomReady = 'true';
    modal.classList.add('hce-inspection-zoom');

    const state = {
      scale: 1,
      x: 0,
      y: 0,
      fitWidth: 0,
      fitHeight: 0,
      dragging: false,
      dragPointerId: null,
      lastX: 0,
      lastY: 0,
      pointers: new Map(),
      pinchStart: null,
      wasOpen: modal.classList.contains('open')
    };

    const controls = document.createElement('div');
    controls.className = 'hce-inspection-zoom-ui';
    controls.setAttribute('aria-label', hceI18n.zoomControls || 'Image zoom controls');
    controls.innerHTML = `
      <div class="hce-inspection-zoom-tip">${hceI18n.zoomTip || 'Scroll / pinch to zoom · drag to inspect'}</div>
      <div class="hce-inspection-zoom-controls">
        <button type="button" class="hce-inspection-zoom-button hce-inspection-zoom-minus" aria-label="${hceI18n.zoomOut || 'Zoom out'}">−</button>
        <span class="hce-inspection-zoom-value" aria-live="polite">100%</span>
        <button type="button" class="hce-inspection-zoom-button hce-inspection-zoom-plus" aria-label="${hceI18n.zoomIn || 'Zoom in'}">+</button>
        <button type="button" class="hce-inspection-zoom-fit" aria-label="${hceI18n.resetZoom || 'Reset image zoom'}">${hceI18n.fit || 'Fit'}</button>
      </div>
    `;
    modal.appendChild(controls);

    const minusButton = controls.querySelector('.hce-inspection-zoom-minus');
    const plusButton = controls.querySelector('.hce-inspection-zoom-plus');
    const fitButton = controls.querySelector('.hce-inspection-zoom-fit');
    const valueLabel = controls.querySelector('.hce-inspection-zoom-value');

    const isDesktop = () => window.innerWidth >= DESKTOP_BREAKPOINT;
    const buttonStep = () => isDesktop() ? DESKTOP_BUTTON_STEP : MOBILE_BUTTON_STEP;

    function getSlides() {
      return Array.from(modal.querySelectorAll('.galSlides'));
    }

    function getActiveSlide() {
      const slides = getSlides();
      return slides.find((slide) => {
        const style = window.getComputedStyle(slide);
        return style.display !== 'none' && style.visibility !== 'hidden';
      }) || slides[0] || null;
    }

    function getActiveImage() {
      const slide = getActiveSlide();
      return slide ? slide.querySelector('img') : null;
    }

    function modalIsOpen() {
      return modal.classList.contains('open');
    }

    function upgradeImageSource(img) {
      if (!img || img.dataset.hceInspectionHighRes === 'true') return;
      const source = img.currentSrc || img.src;
      if (!source) return;

      try {
        const url = new URL(source, window.location.href);
        const width = Number.parseInt(url.searchParams.get('width') || '0', 10);
        if (!width || width < 4000) {
          url.searchParams.set('width', '4000');
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
          img.src = url.toString();
        }
        img.dataset.hceInspectionHighRes = 'true';
      } catch (error) {
        img.dataset.hceInspectionHighRes = 'true';
      }
    }

    function clearDesktopLayout(img) {
      if (!img) return;
      ['position','left','top','width','height','max-width','max-height','margin'].forEach((prop) => img.style.removeProperty(prop));
      state.fitWidth = 0;
      state.fitHeight = 0;
    }

    function layoutDesktopImage() {
      if (!isDesktop()) return true;
      const slide = getActiveSlide();
      const img = getActiveImage();
      if (!slide || !img) return false;

      const stageWidth = slide.clientWidth || modal.clientWidth;
      const stageHeight = slide.clientHeight || modal.clientHeight;
      const naturalWidth = img.naturalWidth || 0;
      const naturalHeight = img.naturalHeight || 0;

      if (!stageWidth || !stageHeight || !naturalWidth || !naturalHeight) {
        if (!img.complete) {
          img.addEventListener('load', () => {
            if (modalIsOpen()) {
              layoutDesktopImage();
              applyTransform();
            }
          }, { once: true });
        }
        return false;
      }

      const fitFactor = Math.min(stageWidth / naturalWidth, stageHeight / naturalHeight);
      state.fitWidth = Math.max(1, naturalWidth * fitFactor);
      state.fitHeight = Math.max(1, naturalHeight * fitFactor);

      img.style.setProperty('position', 'absolute', 'important');
      img.style.setProperty('left', '50%', 'important');
      img.style.setProperty('top', '50%', 'important');
      img.style.setProperty('width', `${state.fitWidth}px`, 'important');
      img.style.setProperty('height', `${state.fitHeight}px`, 'important');
      img.style.setProperty('max-width', 'none', 'important');
      img.style.setProperty('max-height', 'none', 'important');
      img.style.setProperty('margin', '0', 'important');
      return true;
    }

    // HCE smooth-pinch refinement — 2026-09-19
    function prepareActiveImage() {
      const img = getActiveImage();
      if (!img) return false;

      if (img.dataset.hceInspectionPrepared !== 'true') {
        upgradeImageSource(img);
        img.dataset.hceInspectionPrepared = 'true';
      }

      if (isDesktop()) {
        img.dataset.hceMobilePrepared = 'false';
        return layoutDesktopImage();
      }

      if (img.dataset.hceMobilePrepared !== 'true') {
        clearDesktopLayout(img);
        img.dataset.hceMobilePrepared = 'true';
      }
      return true;
    }

    function clearInlineTransforms() {
      modal.querySelectorAll('.galSlides img').forEach((img) => {
        img.style.removeProperty('transform');
        img.style.removeProperty('transform-origin');
        clearDesktopLayout(img);
      });
    }

    function updateControls() {
      valueLabel.textContent = `${Math.round(state.scale * 100)}%`;
      minusButton.disabled = state.scale <= MIN_SCALE + 0.001;
      plusButton.disabled = state.scale >= MAX_SCALE - 0.001;
      fitButton.disabled = state.scale <= MIN_SCALE + 0.001;
      modal.classList.toggle('hce-is-zoomed', state.scale > MIN_SCALE + 0.001);
    }

    function clampPan() {
      const slide = getActiveSlide();
      const img = getActiveImage();
      if (!slide || !img || state.scale <= MIN_SCALE) {
        state.x = 0;
        state.y = 0;
        return;
      }

      const viewportWidth = slide.clientWidth || modal.clientWidth;
      const viewportHeight = slide.clientHeight || modal.clientHeight;
      const imageWidth = isDesktop() ? (state.fitWidth || img.clientWidth) : (img.clientWidth || viewportWidth);
      const imageHeight = isDesktop() ? (state.fitHeight || img.clientHeight) : (img.clientHeight || viewportHeight);

      const maxX = Math.max(0, ((imageWidth * state.scale) - viewportWidth) / 2);
      const maxY = Math.max(0, ((imageHeight * state.scale) - viewportHeight) / 2);

      state.x = Math.min(maxX, Math.max(-maxX, state.x));
      state.y = Math.min(maxY, Math.max(-maxY, state.y));
    }

    let transformFrame = 0;
    function scheduleTransform() {
      if (transformFrame) return;
      transformFrame = window.requestAnimationFrame(() => {
        transformFrame = 0;
        applyTransform();
      });
    }

    function applyTransform() {
      const img = getActiveImage();
      if (!img) return;
      prepareActiveImage();
      clampPan();
      img.style.transformOrigin = '50% 50%';
      img.style.transform = isDesktop()
        ? `translate(-50%, -50%) translate3d(${state.x}px, ${state.y}px, 0) scale(${state.scale})`
        : `translate3d(${state.x}px, ${state.y}px, 0) scale(${state.scale})`;
      updateControls();
    }

    function setScale(nextScale) {
      const img = getActiveImage();
      if (!img) return;
      const clamped = Math.min(MAX_SCALE, Math.max(MIN_SCALE, nextScale));
      state.scale = Math.round(clamped * 100) / 100;

      if (state.scale <= MIN_SCALE + 0.001) {
        state.scale = MIN_SCALE;
        state.x = 0;
        state.y = 0;
      } else {
        upgradeImageSource(img);
      }
      applyTransform();
    }

    function resetZoom(clearTransforms = false) {
      state.scale = MIN_SCALE;
      state.x = 0;
      state.y = 0;
      state.dragging = false;
      state.dragPointerId = null;
      state.pointers.clear();
      state.pinchStart = null;
      if (transformFrame) {
        window.cancelAnimationFrame(transformFrame);
        transformFrame = 0;
      }
      modal.classList.remove('hce-is-dragging', 'hce-is-pinching', 'hce-is-zoomed', 'gallery-mega-zoom');

      if (clearTransforms) {
        clearInlineTransforms();
      } else {
        prepareActiveImage();
        applyTransform();
      }
      updateControls();
    }

    function beginPinch() {
      if (state.pointers.size < 2) return;
      const points = Array.from(state.pointers.values()).slice(0, 2);
      const dx = points[1].x - points[0].x;
      const dy = points[1].y - points[0].y;
      state.pinchStart = {
        distance: Math.max(1, Math.hypot(dx, dy)),
        midpointX: (points[0].x + points[1].x) / 2,
        midpointY: (points[0].y + points[1].y) / 2,
        scale: state.scale,
        x: state.x,
        y: state.y
      };
      state.dragging = false;
      modal.classList.remove('hce-is-dragging');
      modal.classList.add('hce-is-pinching');
    }

    function handlePinch() {
      if (!state.pinchStart || state.pointers.size < 2) return;
      const points = Array.from(state.pointers.values()).slice(0, 2);
      const dx = points[1].x - points[0].x;
      const dy = points[1].y - points[0].y;
      const distance = Math.max(1, Math.hypot(dx, dy));
      const midpointX = (points[0].x + points[1].x) / 2;
      const midpointY = (points[0].y + points[1].y) / 2;

      state.scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, state.pinchStart.scale * (distance / state.pinchStart.distance)));
      state.x = state.pinchStart.x + (midpointX - state.pinchStart.midpointX);
      state.y = state.pinchStart.y + (midpointY - state.pinchStart.midpointY);

      if (state.scale <= MIN_SCALE + 0.01) {
        state.scale = MIN_SCALE;
        state.x = 0;
        state.y = 0;
      } else {
        upgradeImageSource(getActiveImage());
      }
      scheduleTransform();
    }

    minusButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      setScale(state.scale - buttonStep());
    });

    plusButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      setScale(state.scale + buttonStep());
    });

    fitButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      resetZoom();
    });

    modal.addEventListener('wheel', (event) => {
      if (!modalIsOpen() || !event.target.closest('.galSlides img')) return;
      event.preventDefault();
      setScale(state.scale + (event.deltaY < 0 ? WHEEL_STEP : -WHEEL_STEP));
    }, { passive: false });

    modal.addEventListener('dblclick', (event) => {
      if (!modalIsOpen() || !event.target.closest('.galSlides img')) return;
      event.preventDefault();
      event.stopPropagation();
      setScale(state.scale > MIN_SCALE + 0.001 ? MIN_SCALE : 2);
    });

    // HCE mobile stage pinch support — 2026-09-19
    modal.addEventListener('pointerdown', (event) => {
      const slide = event.target.closest('.galSlides.product-media--image');
      const img = slide ? slide.querySelector('img') : null;
      if (!img || !modalIsOpen()) return;
      state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (event.pointerType === 'touch' && state.pointers.size >= 2) {
        event.preventDefault();
        beginPinch();
        return;
      }

      if (state.scale > MIN_SCALE + 0.001) {
        event.preventDefault();
        state.dragging = true;
        state.dragPointerId = event.pointerId;
        state.lastX = event.clientX;
        state.lastY = event.clientY;
        modal.classList.add('hce-is-dragging');
        if (img.setPointerCapture) {
          try { img.setPointerCapture(event.pointerId); } catch (error) {}
        }
      }
    });

    modal.addEventListener('pointermove', (event) => {
      if (!state.pointers.has(event.pointerId)) return;
      state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (state.pointers.size >= 2 && event.pointerType === 'touch') {
        event.preventDefault();
        if (!state.pinchStart) beginPinch();
        handlePinch();
        return;
      }

      if (!state.dragging || state.dragPointerId !== event.pointerId || state.scale <= MIN_SCALE) return;
      event.preventDefault();
      state.x += event.clientX - state.lastX;
      state.y += event.clientY - state.lastY;
      state.lastX = event.clientX;
      state.lastY = event.clientY;
      scheduleTransform();
    });

    function endPointer(event) {
      state.pointers.delete(event.pointerId);
      if (state.pointers.size < 2) {
        state.pinchStart = null;
        modal.classList.remove('hce-is-pinching');
      }
      if (state.dragPointerId === event.pointerId) {
        state.dragging = false;
        state.dragPointerId = null;
        modal.classList.remove('hce-is-dragging');
      }
    }

    modal.addEventListener('pointerup', endPointer);
    modal.addEventListener('pointercancel', endPointer);
    modal.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse' && state.dragPointerId === event.pointerId) endPointer(event);
    });

    // Keep two-finger gestures inside the lightbox. This prevents the browser from
    // magnifying the entire storefront while our image-specific pinch zoom is active.
    modal.addEventListener('touchmove', (event) => {
      if (!modalIsOpen() || event.touches.length < 2) return;
      if (!event.target.closest('.galSlides.product-media--image')) return;
      event.preventDefault();
    }, { passive: false });

    ['gesturestart', 'gesturechange', 'gestureend'].forEach((type) => {
      modal.addEventListener(type, (event) => {
        if (!modalIsOpen()) return;
        if (!event.target.closest('.galSlides.product-media--image')) return;
        event.preventDefault();
      }, { passive: false });
    });

    // Block the theme's legacy two-state image click zoom. The HCE zoom controls,
    // double-tap/double-click and pinch handlers now own image inspection.
    modal.addEventListener('click', (event) => {
      if (event.target.closest('.galSlides.product-media--image')) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);

    modal.addEventListener('click', (event) => {
      if (event.target.closest('.lightbox-thumbnail, .slider-arrow-left, .slider-arrow-right, .modal-prev, .modal-next')) {
        window.setTimeout(() => resetZoom(), 0);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (!modalIsOpen()) return;
      if (event.target && /INPUT|TEXTAREA|SELECT/.test(event.target.tagName)) return;
      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        setScale(state.scale + buttonStep());
      } else if (event.key === '-' || event.key === '_') {
        event.preventDefault();
        setScale(state.scale - buttonStep());
      } else if (event.key === '0') {
        event.preventDefault();
        resetZoom();
      }
    });

    const classObserver = new MutationObserver(() => {
      if (modal.classList.contains('gallery-mega-zoom')) modal.classList.remove('gallery-mega-zoom');
      const isOpen = modal.classList.contains('open');
      if (isOpen && !state.wasOpen) {
        state.wasOpen = true;
        window.setTimeout(() => {
          resetZoom();
          upgradeImageSource(getActiveImage());
        }, 0);
      } else if (!isOpen && state.wasOpen) {
        state.wasOpen = false;
        resetZoom(true);
      }
    });
    classObserver.observe(modal, { attributes: true, attributeFilter: ['class'] });

    const slideObserver = new MutationObserver((mutations) => {
      if (!modalIsOpen()) return;
      const changedSlide = mutations.some((mutation) => mutation.target.classList && mutation.target.classList.contains('galSlides'));
      if (changedSlide) window.setTimeout(() => resetZoom(), 0);
    });
    getSlides().forEach((slide) => slideObserver.observe(slide, { attributes: true, attributeFilter: ['style'] }));

    let resizeTimer;
    window.addEventListener('resize', () => {
      if (!modalIsOpen()) return;
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        state.x = 0;
        state.y = 0;
        if (isDesktop()) layoutDesktopImage();
        else clearDesktopLayout(getActiveImage());
        applyTransform();
      }, 80);
    });

    if (state.wasOpen) {
      resetZoom();
      upgradeImageSource(getActiveImage());
    } else {
      updateControls();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseInspectionZoom, { once: true });
  } else {
    initialiseInspectionZoom();
  }

  document.addEventListener('shopify:section:load', initialiseInspectionZoom);
})();
